/**
 * Vorlagen für Mails von Geschäftsführung und Studioleitung.
 *
 * Jede Vorlage ist einer Mail nachgebaut, die seit August 2026 tatsächlich
 * rausging (Stundenplan-Mails, Korrekturen, Check-in-Erinnerung,
 * Weihnachtsfeier, Kinderturnier, Samurai-Ausstellung) — im selben
 * Haus-Layout. Wer sie nutzt, beantwortet zuerst ein paar Vorab-Fragen
 * (klärt die Abhängigkeiten) und füllt dann wenige Felder aus; den Rest
 * macht der Baukasten (render.ts).
 *
 * Vorab-Fragen haben drei mögliche Folgen:
 *  - ok:      passt, weiter
 *  - hinweis: passt, aber bitte beachten (erscheint auch bei der Redaktion)
 *  - klaeren: offener Punkt — so geht die Mail nicht raus. Die Datenbank
 *             lehnt das Einreichen ab, solange ein offener Punkt besteht.
 */
import type { MailDoc, MailHero, MailSection, FactRow } from './render';
import { offenePlatzhalter } from './platzhalter';
import type { Sportschule } from '../sportschulen';

/**
 * textarea: Text mit Formatierungsleiste (fett, kursiv, markiert, Farben,
 * Größen) · banner: Bannerbild mit Zuschnitt · symbol: Emoji im Kopf.
 */
export type FeldTyp = 'text' | 'textarea' | 'datum' | 'uhrzeit' | 'url' | 'auswahl' | 'zeilen' | 'banner' | 'symbol';
export type Daten = Record<string, string>;
export type Antworten = Record<string, string>;

export interface Feld {
  id: string;
  label: string;
  typ: FeldTyp;
  pflicht?: boolean | ((d: Daten, a: Antworten) => boolean);
  platzhalter?: string;
  hinweis?: string;
  optionen?: { wert: string; label: string }[];
  maxLaenge?: number;
  zeigen?: (d: Daten, a: Antworten) => boolean;
  /** Gestaltung (Banner, Symbol, Emojis) steht im Assistenten in einem eigenen Kasten. */
  gruppe?: 'gestaltung';
  /**
   * typ 'symbol': das Symbol, das zur Vorlage passt. typ 'auswahl': der Wert,
   * der gilt, solange nichts gespeichert ist (z. B. bei älteren Entwürfen).
   */
  standard?: string;
}

export interface FrageOption { wert: string; label: string; folge?: 'ok' | 'hinweis' | 'klaeren'; text?: string }

export interface Frage {
  id: string;
  frage: string;
  /** Warum wir fragen — welche Abhängigkeit dahintersteht. */
  warum: string;
  optionen: FrageOption[];
  zeigen?: (a: Antworten) => boolean;
}

export interface Kontext {
  /** Gewählte Sportschulen; leer = alle Sportschulen. */
  schulen: Sportschule[];
  /** Wohin Antworten gehen sollen (Studio-Postfach, Backoffice …) */
  kontaktEmail: string;
}

export type Kategorie = 'kurzfristig' | 'stundenplan' | 'einladung' | 'erinnerung' | 'eltern' | 'pruefung' | 'info';

export interface Vorlage {
  id: string;
  name: string;
  /** Ein Satz: wofür */
  kurz: string;
  /** Wann nimmt man sie — und wann nicht */
  wann: string;
  emoji: string;
  kategorie: Kategorie;
  /** Mails, nach deren Vorbild die Vorlage gebaut ist */
  vorbild: string[];
  vorlauf: string;
  fragen: Frage[];
  felder: Feld[];
  /** Startwerte beim Anlegen (auch Beispielwerte für die Vorschau in der Galerie) */
  start: (k: Kontext) => Daten;
  betreff: (d: Daten, k: Kontext) => string[];
  vorschautext: (d: Daten, k: Kontext) => string;
  /** Antworten auf die Vorab-Fragen fließen dort ein, wo sie die Mail prägen (z. B. „Zuschauen" bei der Prüfung). */
  bauen: (d: Daten, k: Kontext, a?: Antworten) => MailDoc;
}

// ── Hilfen ──────────────────────────────────────────────────────────────────

const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

/** „2026-09-30" → „Dienstag, 30.09.2026" */
export function datumLang(iso: string | undefined): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [j, m, t] = iso.split('-').map(Number);
  const d = new Date(j, m - 1, t);
  return `${WOCHENTAGE[d.getDay()]}, ${String(t).padStart(2, '0')}.${String(m).padStart(2, '0')}.${j}`;
}

/** „2026-09-30" → „30.09." */
export function datumKurz(iso: string | undefined): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [, m, t] = iso.split('-');
  return `${t}.${m}.`;
}

/** „17:00" → „17:00 Uhr"; freie Angaben („17:00–18:00") bleiben, bekommen „Uhr". */
export function uhr(s: string | undefined): string {
  const t = (s ?? '').trim();
  if (!t) return '';
  return /uhr$/i.test(t) ? t : `${t} Uhr`;
}

function zeilen(s: string | undefined): string[] {
  return (s ?? '').split(/\r?\n/).map((z) => z.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
}

/** „Titel: Text" je Zeile → Karten */
function karten(s: string | undefined): Array<{ title: string; text: string }> {
  return zeilen(s).map((z) => {
    const i = z.indexOf(':');
    return i > 0 ? { title: z.slice(0, i).trim(), text: z.slice(i + 1).trim() } : { title: z, text: '' };
  });
}

function ortText(k: Kontext): string {
  if (k.schulen.length === 1) return k.schulen[0].name;
  if (k.schulen.length === 0 || k.schulen.length === 7) return 'alle Sportschulen';
  return k.schulen.map((s) => s.name).join(', ');
}

// ── Emojis ──────────────────────────────────────────────────────────────────
// Jede Mail wahlweise ohne Emojis, normal (Symbol im Kopf, Gruß und
// Signatur) oder mit vielen, passend zum Thema (auch Überschriften,
// Eckdaten, Hinweiszeilen). Anforderung vom 29.09.2026.

export type EmojiStufe = 'ohne' | 'normal' | 'viele';

export const EMOJI_STUFEN: { wert: EmojiStufe; label: string; text: string }[] = [
  { wert: 'ohne', label: 'Ohne Emojis', text: 'ganz schlicht, auch ohne Symbol im Kopf (außer Du wählst eins)' },
  { wert: 'normal', label: 'Normal', text: 'ein Symbol im Kopf, dazu 👋 im Gruß und 🥋 in der Signatur' },
  { wert: 'viele', label: 'Viele, passend zum Thema', text: 'zusätzlich in Überschriften, Eckdaten und Hinweiszeilen' },
];

export function emojiStufe(d: Daten): EmojiStufe {
  return d.emojis === 'ohne' || d.emojis === 'viele' ? d.emojis : 'normal';
}

/** Emoji mit Leerzeichen — nur bei der Stufe „viele". */
function em(d: Daten, emoji: string): string {
  return emojiStufe(d) === 'viele' ? `${emoji} ` : '';
}

const EMOJI_RE = /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u200d|\ufe0f|\u20e3/gu;

export function hatEmoji(s: string | undefined): boolean {
  return /\p{Extended_Pictographic}/u.test(s ?? '');
}

export function ohneEmojis(s: string | undefined): string {
  return (s ?? '').replace(EMOJI_RE, '').replace(/[ \t]{2,}/g, ' ').replace(/\s+([,.!?])/g, '$1').trim();
}

/** Symbol im Kopf: eigenes, keins oder passend zur Vorlage (normal: eins, viele: zwei). */
function kopfSymbol(d: Daten, einfach: string, viele: string): string {
  const wahl = (d.symbol ?? d.emoji ?? '').trim();
  if (wahl === 'kein') return '';
  if (wahl && wahl !== 'auto') return wahl;
  const st = emojiStufe(d);
  return st === 'ohne' ? '' : st === 'viele' ? viele : einfach;
}

/** Signatur „Euer Randori-Pro Team …" — 🥋 außer bei „ohne". */
function teamName(k: Kontext, d: Daten): string {
  const team = k.schulen.length === 1 ? `Euer Randori-Pro Team ${k.schulen[0].name}` : 'Euer Randori-Pro Team';
  return emojiStufe(d) === 'ohne' ? team : `${team} 🥋`;
}

/** Schlusssatz: bei „ohne" ohne Emojis, bei „viele" mit 🥋, sonst wie geschrieben. */
function schluss(d: Daten): string {
  const t = (d.schluss ?? '').trim();
  if (!t) return '';
  const st = emojiStufe(d);
  if (st === 'ohne') return ohneEmojis(t);
  if (st === 'viele' && !hatEmoji(t)) return `${t} 🥋`;
  return t;
}

/** Eckdaten: bei „viele" ein Symbol vor die bekannten Beschriftungen. */
const FAKTEN_SYMBOL: Record<string, string> = {
  Was: '📌', Kurs: '🥋', Wann: '📅', Datum: '📅', Wo: '📍', 'Für wen': '👥', Kosten: '💶', Gebühr: '💶',
  Anmeldeschluss: '⏰', Bis: '⏰', Einlass: '🚪', Anlass: '📌', Geschlossen: '🔒', 'Wieder da': '✅', Worum: '📌',
};
function fakten(d: Daten, rows: FactRow[]): FactRow[] {
  if (emojiStufe(d) !== 'viele') return rows;
  return rows.map((r) => (FAKTEN_SYMBOL[r.label] ? { ...r, label: `${FAKTEN_SYMBOL[r.label]} ${r.label}` } : r));
}

/** Bannerbild aus den Feldern bild (fertig zugeschnitten) und bild_meta. */
export interface BannerMeta {
  /** Das hochgeladene Original, damit sich der Ausschnitt später ändern lässt */
  quelle: string;
  /** Mittelpunkt des Ausschnitts im Original (0–1) */
  x: number;
  y: number;
  /** 1 = Bild füllt den Rahmen, größer = näher heran */
  zoom: number;
  /** Höhe in der Mail bei 620 px Breite */
  hoehe: number;
  platz: 'oben' | 'unten';
}

export function bannerMeta(d: Daten): BannerMeta | null {
  try {
    const m = JSON.parse(d.bild_meta || 'null') as Partial<BannerMeta> | null;
    if (!m || typeof m.quelle !== 'string') return null;
    return {
      quelle: m.quelle,
      x: typeof m.x === 'number' ? m.x : 0.5,
      y: typeof m.y === 'number' ? m.y : 0.5,
      zoom: typeof m.zoom === 'number' ? m.zoom : 1,
      hoehe: typeof m.hoehe === 'number' ? m.hoehe : 280,
      platz: m.platz === 'oben' ? 'oben' : 'unten',
    };
  } catch {
    return null;
  }
}

function banner(d: Daten, alt: string): Pick<MailHero, 'imageUrl' | 'imageAlt' | 'imageHeight' | 'imagePosition'> {
  if (!d.bild?.trim()) return {};
  const m = bannerMeta(d);
  return { imageUrl: d.bild.trim(), imageAlt: alt, imageHeight: m?.hoehe ?? 280, imagePosition: m?.platz ?? 'unten' };
}

function kontakt(k: Kontext): MailDoc['contact'] {
  if (k.schulen.length === 1) return { email: k.kontaktEmail || k.schulen[0].email, place: k.schulen[0].name };
  if (k.kontaktEmail && k.kontaktEmail !== 'backoffice@randori-pro.de') return { email: k.kontaktEmail };
  // Mehrere Sportschulen: kein einzelnes Postfach (Regel aus dem Mail-Skill).
  return { text: 'Bei Fragen wendet Euch bitte an den Tresen Eurer Sportschule.' };
}

export const ANREDEN: { wert: string; label: string; text: string }[] = [
  { wert: 'mitglieder-eltern', label: 'Liebe Mitglieder, liebe Eltern', text: 'Liebe Mitglieder, liebe Eltern' },
  { wert: 'eltern', label: 'Liebe Eltern', text: 'Liebe Eltern' },
  { wert: 'mitglieder', label: 'Liebe Mitglieder', text: 'Liebe Mitglieder' },
  { wert: 'familie', label: 'Hallo liebe Familie', text: 'Hallo liebe Familie' },
  { wert: 'vorname', label: 'Hey Vorname (persönlich)', text: 'Hey {{ contact.VORNAME }}' },
];

/** Anrede mit 👋 (außer bei „ohne Emojis"). */
function anrede(d: Daten): string {
  const text = ANREDEN.find((a) => a.wert === d.anrede)?.text ?? ANREDEN[0].text;
  return emojiStufe(d) === 'ohne' ? `${text},` : `${text} 👋,`;
}

const FELD_ANREDE: Feld = {
  id: 'anrede',
  label: 'Anrede',
  typ: 'auswahl',
  optionen: ANREDEN.map((a) => ({ wert: a.wert, label: a.label })),
  hinweis: 'Bei Kindern geht die Mail an das Postfach der Eltern. „Hey Vorname" nur bei Mails an Erwachsene — in Familien-Postfächern steht oft der Name des Kindes.',
};

const FELD_BANNER: Feld = {
  id: 'bild',
  label: 'Bannerbild (freiwillig)',
  typ: 'banner',
  gruppe: 'gestaltung',
  hinweis: 'Ein Foto quer über die ganze Breite. Nach dem Hochladen verschiebst und vergrößerst Du den Ausschnitt; höher als 300 Pixel wird der Banner nicht, damit der Kopf der Mail sichtbar bleibt. Ohne Bild wirkt die Mail genauso — der rote Kopf trägt sie.',
};

/** Emojis zur Auswahl für das Symbol im Kopf (dazu: passend zur Vorlage, eigenes, keins). */
export const SYMBOLE: { wert: string; label: string }[] = [
  { wert: '🥋', label: 'Training' }, { wert: '📅', label: 'Termin' }, { wert: '⚡', label: 'Wichtig' },
  { wert: '🔔', label: 'Erinnerung' }, { wert: '📣', label: 'Ankündigung' }, { wert: '📰', label: 'Neuigkeiten' },
  { wert: '🎉', label: 'Feier' }, { wert: '🏆', label: 'Erfolg' }, { wert: '⭐', label: 'Prüfung' },
  { wert: '💪', label: 'Motivation' }, { wert: '🥊', label: 'Wettkampf' }, { wert: '🎒', label: 'Ausflug' },
  { wert: '🏕️', label: 'Camp' }, { wert: '☀️', label: 'Ferien' }, { wert: '🎄', label: 'Weihnachten' },
  { wert: '❤️', label: 'Danke' }, { wert: '👨‍👩‍👧', label: 'Familie' }, { wert: '🔒', label: 'Geschlossen' },
];

function feldSymbol(standard: string): Feld {
  return {
    id: 'symbol', label: 'Symbol im Kopf', typ: 'symbol', gruppe: 'gestaltung', standard,
    hinweis: 'Das Emoji über der Überschrift. „Passend zur Vorlage" richtet sich nach der Emoji-Stufe; Du kannst auch ein eigenes einsetzen oder keins.',
  };
}

const FELD_EMOJIS: Feld = {
  id: 'emojis', label: 'Emojis in der Mail', typ: 'auswahl', gruppe: 'gestaltung', pflicht: true, standard: 'normal',
  optionen: EMOJI_STUFEN.map((e) => ({ wert: e.wert, label: `${e.label} — ${e.text}` })),
};

/** Bannerbild, Symbol und Emoji-Stufe — bei jeder Vorlage gleich. */
function gestaltung(standard: string): Feld[] {
  return [FELD_BANNER, feldSymbol(standard), FELD_EMOJIS];
}

/** Startwerte der Gestaltung: kein Banner, Symbol passend zur Vorlage, Emojis normal. */
const START_GESTALTUNG: Daten = { bild: '', bild_meta: '', symbol: '', emojis: 'normal' };

const FELD_SCHLUSS: Feld = {
  id: 'schluss',
  label: 'Schlusssatz',
  typ: 'text',
  maxLaenge: 160,
  platzhalter: 'z. B. Wir sehen uns auf der Matte!',
};

/** Fragen, die jede Mail vorher beantworten muss. */
const FRAGEN_IMMER: Frage[] = [
  {
    id: 'abgestimmt',
    frage: 'Ist der Inhalt mit allen Beteiligten abgestimmt?',
    warum: 'Die Redaktion prüft Sprache, Form und Empfänger — nicht, ob der Termin mit Trainer:innen, Studioleitung oder Geschäftsführung geklärt ist. Das muss vorher stehen.',
    optionen: [
      { wert: 'ja', label: 'Ja, alles abgestimmt', folge: 'ok' },
      { wert: 'nein', label: 'Noch nicht', folge: 'klaeren', text: 'Erst mit den Beteiligten abstimmen, dann einreichen. Dein Entwurf bleibt so lange gespeichert.' },
    ],
  },
  {
    id: 'fakten',
    frage: 'Stehen alle Termine, Uhrzeiten, Preise und Links fest?',
    warum: 'Eine Korrekturmail verunsichert mehr als eine Mail, die einen Tag später kommt. Im August mussten zwei Stundenplan-Mails korrigiert werden — genau das vermeiden wir.',
    optionen: [
      { wert: 'ja', label: 'Ja, alles steht fest', folge: 'ok' },
      { wert: 'nein', label: 'Noch nicht alles', folge: 'klaeren', text: 'Mit offenen Angaben geht keine Mail raus. Trag sie ein, sobald alles feststeht.' },
    ],
  },
];

// ── Die Vorlagen ────────────────────────────────────────────────────────────

const KURZFRISTIG: Vorlage = {
  id: 'kurzfristige-aenderung',
  name: 'Training fällt aus / kurzfristige Änderung',
  kurz: 'Ein Kurs fällt aus, beginnt später, zieht um oder ein anderer Trainer übernimmt.',
  wann: 'Für Änderungen in den nächsten Tagen. Für einen ganz neuen Stundenplan nimm „Stundenplan-Änderung", für geschlossene Tage „Schließzeit".',
  emoji: '📅⚡',
  kategorie: 'kurzfristig',
  vorbild: ['Korrektur: Dienstags-Öffnung Mariendorf (21.08.2026)', 'Stundenplan Steglitz: kleine Anpassungen (28.08.2026)'],
  vorlauf: 'Geht auch kurzfristig — markier die Mail dann als „eilt".',
  fragen: [
    {
      id: 'ersatz',
      frage: 'Gibt es einen Ersatztermin?',
      warum: 'Die erste Frage am Tresen ist immer: „Und wann dann?" Steht die Antwort in der Mail, bleibt es dort ruhig.',
      optionen: [
        { wert: 'ja', label: 'Ja', folge: 'ok' },
        { wert: 'nein', label: 'Nein, der Termin entfällt ersatzlos', folge: 'ok' },
        { wert: 'offen', label: 'Steht noch nicht fest', folge: 'hinweis', text: 'Dann schreib „Den Ersatztermin geben wir bald bekannt" — und denk an die zweite Mail, sobald er steht.' },
      ],
    },
    {
      id: 'probe',
      frage: 'Sind Probetrainings oder Einweisungen betroffen?',
      warum: 'Die Mail erreicht nur Mitglieder. Wer zum Probetraining oder zur Milon-Einweisung gebucht ist, erfährt davon nur über das CRM.',
      optionen: [
        { wert: 'nein', label: 'Nein', folge: 'ok' },
        { wert: 'ja', label: 'Ja', folge: 'hinweis', text: 'Bitte die betroffenen Termine im CRM absagen oder umbuchen — sonst stehen Interessierte vor verschlossener Tür.' },
        { wert: 'weiss-nicht', label: 'Weiß ich nicht', folge: 'hinweis', text: 'Schau im CRM unter Probetermine nach, ob an dem Tag etwas gebucht ist.' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    {
      id: 'art', label: 'Was ändert sich?', typ: 'auswahl', pflicht: true,
      optionen: [
        { wert: 'faellt-aus', label: 'Training fällt aus' },
        { wert: 'verschoben', label: 'Training verschoben' },
        { wert: 'raum', label: 'Anderer Raum / andere Halle' },
        { wert: 'trainer', label: 'Vertretung durch andere:n Trainer:in' },
        { wert: 'sonstiges', label: 'Etwas anderes' },
      ],
    },
    { id: 'kurs', label: 'Welcher Kurs?', typ: 'text', pflicht: true, platzhalter: 'z. B. Kids Kickboxen 6–9 Jahre', maxLaenge: 80 },
    { id: 'datum', label: 'Datum', typ: 'datum', pflicht: true },
    { id: 'uhrzeit', label: 'Uhrzeit', typ: 'text', pflicht: true, platzhalter: 'z. B. 17:00–18:00', maxLaenge: 40 },
    { id: 'grund', label: 'Grund (freiwillig)', typ: 'text', platzhalter: 'z. B. Unser Trainer ist krank', maxLaenge: 120, hinweis: 'Kurz und sachlich — keine Gesundheitsdetails.' },
    {
      id: 'ersatztext', label: 'Ersatztermin / Alternative', typ: 'textarea',
      platzhalter: 'z. B. Nachholtermin am **Freitag, 03.10., 17:00 Uhr**. Oder: Ihr könnt gern in den Kurs am Donnerstag kommen.',
      pflicht: (_d, a) => a.ersatz === 'ja',
      zeigen: (_d, a) => a.ersatz !== 'nein',
    },
    { id: 'zusatz', label: 'Noch etwas Wichtiges? (freiwillig)', typ: 'textarea', platzhalter: 'Erscheint als rot umrandeter Hinweis.' },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('📅'),
  ],
  start: () => ({ ...START_GESTALTUNG,
    art: 'faellt-aus', kurs: '', datum: '', uhrzeit: '', grund: '', ersatztext: '', zusatz: '',
    anrede: 'mitglieder-eltern', schluss: 'Danke für Euer Verständnis — wir sehen uns auf der Matte!',
  }),
  betreff: (d) => {
    const was = TITEL_KURZ[d.art] ?? 'Änderung';
    const wann = d.datum ? datumKurz(d.datum) : '';
    const kurs = d.kurs || 'Training';
    return [
      `${was}: ${kurs} am ${wann}`.replace(/ am $/, ''),
      `Wichtig für ${wann || 'diese Woche'}: ${kurs}`,
      `${em(d, '⚡')}Kurzfristige Änderung – ${kurs}`,
    ];
  },
  vorschautext: (d) => [datumLang(d.datum), uhr(d.uhrzeit), d.kurs].filter(Boolean).join(' · '),
  bauen: (d, k) => {
    const was = TITEL_KURZ[d.art] ?? 'Kurzfristige Änderung';
    const facts: FactRow[] = [
      { label: 'Was', value: was },
      { label: 'Kurs', value: d.kurs },
      { label: 'Wann', value: [datumLang(d.datum), uhr(d.uhrzeit)].filter(Boolean).join(' · ') },
      { label: 'Wo', value: k.schulen.length === 1 ? `Randori-Pro ${k.schulen[0].name}` : '', sub: k.schulen.length === 1 ? k.schulen[0].adresse : '' },
    ];
    const satz: Record<string, string> = {
      'faellt-aus': `fällt das Training **${d.kurs}** am **${datumLang(d.datum)}** (${uhr(d.uhrzeit)}) leider aus.`,
      verschoben: `verschiebt sich das Training **${d.kurs}** am **${datumLang(d.datum)}** — neue Zeit: **${uhr(d.uhrzeit)}**.`,
      raum: `findet das Training **${d.kurs}** am **${datumLang(d.datum)}** (${uhr(d.uhrzeit)}) an einem anderen Ort statt.`,
      trainer: `übernimmt beim Training **${d.kurs}** am **${datumLang(d.datum)}** (${uhr(d.uhrzeit)}) eine Vertretung.`,
      sonstiges: `ändert sich beim Training **${d.kurs}** am **${datumLang(d.datum)}** (${uhr(d.uhrzeit)}) etwas.`,
    };
    const intro = [`kurze Info vorab: ${satz[d.art] ?? satz.sonstiges}`, d.grund ? `${d.grund.trim().replace(/\.?$/, '.')}` : ''].filter(Boolean).join(' ');
    const sections: MailSection[] = [{ type: 'facts', heading: `${em(d, '📋')}Das Wichtigste`, rows: fakten(d, facts) }];
    if (d.ersatztext?.trim()) sections.push({ type: 'box', title: `${em(d, '🔁')}So geht es weiter`, text: d.ersatztext });
    if (d.zusatz?.trim()) sections.push({ type: 'box', tone: 'important', title: `${em(d, '⚠️')}Bitte beachten`, text: d.zusatz });
    return {
      title: `${was} – ${d.kurs}`,
      preheader: [datumLang(d.datum), uhr(d.uhrzeit), d.kurs].filter(Boolean).join(' · '),
      hero: { emoji: kopfSymbol(d, '📅', '📅⚡'), badge: `Kurzfristige Änderung · ${ortText(k)}`, title: was, subtitle: d.kurs, highlight: `${em(d, '⚡')}${[datumLang(d.datum), uhr(d.uhrzeit)].filter(Boolean).join(' · ')}`, ...banner(d, was) },
      greeting: anrede(d),
      intro,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

const TITEL_KURZ: Record<string, string> = {
  'faellt-aus': 'Training fällt aus',
  verschoben: 'Training verschoben',
  raum: 'Anderer Trainingsort',
  trainer: 'Vertretung im Training',
  sonstiges: 'Kurzfristige Änderung',
};

const STUNDENPLAN: Vorlage = {
  id: 'stundenplan',
  name: 'Stundenplan-Änderung',
  kurz: 'Neuer Stundenplan oder mehrere Änderungen ab einem Stichtag.',
  wann: 'Wenn sich ab einem Datum dauerhaft etwas ändert — neue Kurse, neue Zeiten, ein ganzer neuer Plan. Für einen einzelnen ausfallenden Termin nimm „Training fällt aus".',
  emoji: '🥋📅',
  kategorie: 'stundenplan',
  vorbild: ['Stundenplan-Mails aller sieben Standorte (19.08.2026)', 'Stundenplan Steglitz: kleine Anpassungen (28.08.2026)'],
  vorlauf: 'Mindestens vier Werktage vor dem Stichtag einreichen.',
  fragen: [
    {
      id: 'crm',
      frage: 'Ist der neue Stundenplan schon im CRM eingetragen?',
      warum: 'Check-in am Terminal, Probetraining-Buchung, Kurs-Erinnerungen und die Website lesen den Stundenplan aus dem CRM. Steht in der Mail etwas anderes als am Terminal, gibt es Chaos am Tresen.',
      optionen: [
        { wert: 'ja', label: 'Ja, eingetragen', folge: 'ok' },
        { wert: 'nein', label: 'Noch nicht', folge: 'klaeren', text: 'Bitte zuerst im CRM unter „Stundenplan" eintragen — oder die Studioleitung bzw. das Backoffice darum bitten. Dann einreichen.' },
      ],
    },
    {
      id: 'pdf',
      frage: 'Gibt es den Stundenplan als PDF zum Herunterladen?',
      warum: 'Viele speichern den Plan aufs Handy oder hängen ihn an den Kühlschrank — der Knopf „Stundenplan ansehen" war in den August-Mails der meistgeklickte.',
      optionen: [
        { wert: 'ja', label: 'Ja, mit Link', folge: 'ok' },
        { wert: 'nein', label: 'Nein', folge: 'ok' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    { id: 'ab', label: 'Gilt ab', typ: 'datum', pflicht: true },
    { id: 'titel', label: 'Überschrift', typ: 'text', pflicht: true, maxLaenge: 70, platzhalter: 'z. B. Euer neuer Stundenplan ist da' },
    { id: 'einleitung', label: 'Einleitung', typ: 'textarea', pflicht: true, platzhalter: 'Ein, zwei Sätze: Was ändert sich und warum?' },
    { id: 'aenderungen', label: 'Die Änderungen (eine je Zeile)', typ: 'zeilen', platzhalter: 'Mo 17:00 Kids 6–9 → neu 17:30\nNeu: Do 18:00 MMA Teens', hinweis: 'Kurz und eindeutig: Tag, Uhrzeit, Kurs, was neu ist.' },
    { id: 'pdf', label: 'Link zum Stundenplan (PDF)', typ: 'url', platzhalter: 'https://drive.google.com/…', pflicht: (_d, a) => a.pdf === 'ja', zeigen: (_d, a) => a.pdf !== 'nein' },
    { id: 'checkin', label: 'Hinweis zum Check-in am Terminal', typ: 'auswahl', optionen: [{ wert: 'ja', label: 'Ja, mit aufnehmen' }, { wert: 'nein', label: 'Nein' }] },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('📅'),
  ],
  start: () => ({ ...START_GESTALTUNG,
    ab: '', titel: 'Euer Stundenplan: Das ändert sich', einleitung: '', aenderungen: '', pdf: '', checkin: 'ja',
    anrede: 'mitglieder-eltern', schluss: 'Wir freuen uns auf Euch — bis bald auf der Matte!',
  }),
  betreff: (d, k) => [
    `Stundenplan ${ortText(k)}: Änderungen ab ${datumKurz(d.ab) || 'Montag'}`,
    `${em(d, '🥋')}Euer Stundenplan für ${ortText(k)} – gültig ab ${datumKurz(d.ab) || 'bald'}`,
    `Neue Trainingszeiten in ${ortText(k)} ab ${datumLang(d.ab) || 'Montag'}`,
  ],
  vorschautext: (d) => `Gilt ab ${datumLang(d.ab) || 'dem Stichtag'} — alle Änderungen auf einen Blick.`,
  bauen: (d, k) => {
    const sections: MailSection[] = [];
    const liste = zeilen(d.aenderungen);
    if (liste.length) sections.push({ type: 'list', heading: `${em(d, '📅')}Was sich ändert`, items: liste });
    if (d.pdf?.trim()) sections.push({ type: 'cta', title: 'Stundenplan als PDF', text: 'Die ganze Woche auf einer Seite — zum Speichern auf dem Handy oder zum Ausdrucken.', buttonText: 'Stundenplan ansehen & herunterladen', url: d.pdf });
    if (d.checkin === 'ja') sections.push({ type: 'box', tone: 'important', title: `${em(d, '🪪')}Hinweis: Einchecken am Terminal`, text: 'Bitte vorne am Empfang **Euer Armband oder Eure Karte ans Terminal halten** und den Kurs auswählen, an dem Ihr teilnehmt. So sieht unser Trainer direkt, wer da ist.' });
    sections.push({ type: 'box', title: `${em(d, '📌')}Fragen zu Kursen & Zeiten?`, text: k.schulen.length === 1 ? `Bei Fragen zu Kursen, Zeiten oder der richtigen Gruppe wendet Euch einfach an den Tresen in ${k.schulen[0].name}.` : 'Bei Fragen zu Kursen, Zeiten oder der richtigen Gruppe wendet Euch einfach an den Tresen Eurer Sportschule.' });
    return {
      title: d.titel || 'Stundenplan',
      preheader: `Gilt ab ${datumLang(d.ab)}`,
      hero: { emoji: kopfSymbol(d, '📅', '🥋📅'), badge: `Stundenplan · ${ortText(k)}`, title: d.titel || 'Euer neuer Stundenplan', highlight: d.ab ? `${em(d, '⚡')}Gilt ab ${datumLang(d.ab)}` : undefined, ...banner(d, 'Training bei Randori-Pro') },
      greeting: anrede(d),
      intro: d.einleitung,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

const SCHLIESSZEIT: Vorlage = {
  id: 'schliesszeit',
  name: 'Schließzeit oder Feiertag',
  kurz: 'Die Sportschule bleibt an einem oder mehreren Tagen geschlossen.',
  wann: 'Ferien, Feiertage, Renovierung, Wasserschaden. Fällt nur ein einzelner Kurs aus, nimm „Training fällt aus".',
  emoji: '📅🔒',
  kategorie: 'kurzfristig',
  vorbild: ['Schließzeiten im CRM (seit 28.09.2026 für Probetraining und Kurs-Erinnerungen maßgeblich)'],
  vorlauf: 'Möglichst eine Woche vorher — kurzfristig mit „eilt".',
  fragen: [
    {
      id: 'crm',
      frage: 'Ist die Schließzeit im CRM eingetragen?',
      warum: 'Seit dem 28.09. wirken Schließzeiten im CRM wie Feiertage: Niemand bucht dann ein Probetraining in die geschlossenen Tage, und die Kurs-Erinnerungen fallen aus. Ohne Eintrag stehen Interessierte vor verschlossener Tür.',
      optionen: [
        { wert: 'ja', label: 'Ja, eingetragen', folge: 'ok' },
        { wert: 'nein', label: 'Noch nicht', folge: 'klaeren', text: 'Bitte zuerst im CRM unter „Schließzeiten" eintragen (oder eintragen lassen), dann einreichen.' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    { id: 'anlass', label: 'Anlass', typ: 'text', pflicht: true, maxLaenge: 60, platzhalter: 'z. B. Herbstferien, Tag der Deutschen Einheit, Renovierung' },
    { id: 'von', label: 'Geschlossen von', typ: 'datum', pflicht: true },
    { id: 'bis', label: 'bis einschließlich', typ: 'datum', pflicht: true },
    { id: 'wieder', label: 'Erstes Training danach', typ: 'datum', pflicht: true },
    { id: 'ausnahmen', label: 'Ausnahmen (freiwillig)', typ: 'textarea', platzhalter: 'z. B. Das Wettkampftraining am Samstag findet trotzdem statt.' },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('🔒'),
  ],
  start: () => ({ ...START_GESTALTUNG, anlass: '', von: '', bis: '', wieder: '', ausnahmen: '', anrede: 'mitglieder-eltern', schluss: 'Wir wünschen Euch eine schöne Zeit — bis bald auf der Matte!' }),
  betreff: (d, k) => [
    `${ortText(k)} geschlossen: ${d.anlass || 'Schließzeit'} ${datumKurz(d.von)}–${datumKurz(d.bis)}`,
    `${em(d, '🔒')}${d.anlass || 'Schließzeit'}: Training ab ${datumKurz(d.wieder) || '—'} wieder wie gewohnt`,
    `Kurze Pause: ${ortText(k)} bleibt vom ${datumKurz(d.von)} bis ${datumKurz(d.bis)} zu`,
  ],
  vorschautext: (d) => `Geschlossen ${datumKurz(d.von)}–${datumKurz(d.bis)} · ab ${datumLang(d.wieder)} geht es weiter.`,
  bauen: (d, k) => {
    const gleich = d.von && d.von === d.bis;
    const zeitraum = gleich ? datumLang(d.von) : `${datumLang(d.von)} bis ${datumLang(d.bis)}`;
    const sections: MailSection[] = [
      { type: 'facts', heading: `${em(d, '📋')}Alle Infos auf einen Blick`, rows: fakten(d, [
        { label: 'Anlass', value: d.anlass },
        { label: 'Geschlossen', value: zeitraum },
        { label: 'Wieder da', value: datumLang(d.wieder) },
        { label: 'Wo', value: k.schulen.length === 1 ? `Randori-Pro ${k.schulen[0].name}` : ortText(k) },
      ]) },
    ];
    if (d.ausnahmen?.trim()) sections.push({ type: 'box', tone: 'important', title: `${em(d, '⚠️')}Ausnahme`, text: d.ausnahmen });
    return {
      title: `${d.anlass || 'Schließzeit'} – ${ortText(k)}`,
      preheader: `Geschlossen: ${zeitraum}`,
      hero: { emoji: kopfSymbol(d, '🔒', '📅🔒'), badge: `${d.anlass || 'Schließzeit'} · ${ortText(k)}`, title: gleich ? 'Heute bleibt die Matte leer' : 'Wir machen eine kurze Pause', subtitle: zeitraum, highlight: d.wieder ? `${em(d, '⚡')}Ab ${datumLang(d.wieder)} wieder Training` : undefined, ...banner(d, d.anlass || 'Schließzeit') },
      greeting: anrede(d),
      intro: `wegen **${d.anlass}** bleibt ${k.schulen.length === 1 ? `unsere Sportschule in ${k.schulen[0].name}` : 'unsere Sportschule'} ${gleich ? `am **${datumLang(d.von)}**` : `vom **${datumLang(d.von)}** bis einschließlich **${datumLang(d.bis)}**`} geschlossen. Ab **${datumLang(d.wieder)}** trainieren wir wieder nach dem gewohnten Stundenplan.`,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

const ERINNERUNG: Vorlage = {
  id: 'erinnerung',
  name: 'Erinnerung',
  kurz: 'An etwas erinnern: Check-in, Anmeldeschluss, Gebühr, freie Plätze, Ausrüstung.',
  wann: 'Wenn alle es schon einmal gehört haben und es nur noch einen Schubs braucht.',
  emoji: '🔔',
  kategorie: 'erinnerung',
  vorbild: ['Check-in am Terminal – Erinnerung Spandau (11.09.2026)', 'Kinderturnier: Startgebühr in bar mitbringen (24.09.2026)', 'Notwehr-Seminar: Noch Plätze frei (Entwurf 29.09.2026)'],
  vorlauf: 'Vier Werktage vorher einreichen; mit Frist rechtzeitig vor der Frist.',
  fragen: [
    {
      id: 'frist',
      frage: 'Gibt es eine Frist oder einen Stichtag?',
      warum: 'Mit Datum wirkt eine Erinnerung — ohne wird sie leicht überlesen.',
      optionen: [
        { wert: 'ja', label: 'Ja', folge: 'ok' },
        { wert: 'nein', label: 'Nein', folge: 'ok' },
      ],
    },
    {
      id: 'link',
      frage: 'Soll ein Knopf in der Mail stehen (z. B. zur Anmeldung)?',
      warum: 'Ein Knopf führt direkt ans Ziel. Er muss aber funktionieren, bevor die Mail rausgeht.',
      optionen: [
        { wert: 'nein', label: 'Nein', folge: 'ok' },
        { wert: 'ja', label: 'Ja, und der Link funktioniert', folge: 'ok' },
        { wert: 'bald', label: 'Ja, aber der Link ist noch nicht fertig', folge: 'klaeren', text: 'Die Mail geht erst raus, wenn der Link funktioniert — sonst landen die Fragen am Tresen.' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    { id: 'thema', label: 'Woran wird erinnert?', typ: 'text', pflicht: true, maxLaenge: 60, platzhalter: 'z. B. Check-in am Terminal' },
    { id: 'titel', label: 'Überschrift', typ: 'text', pflicht: true, maxLaenge: 70, platzhalter: 'z. B. Bitte denkt an den Check-in' },
    { id: 'text', label: 'Text', typ: 'textarea', pflicht: true, platzhalter: 'Worum geht es, was sollen alle tun? **fett** für das Wichtigste.' },
    { id: 'frist', label: 'Frist / Stichtag', typ: 'datum', pflicht: (_d, a) => a.frist === 'ja', zeigen: (_d, a) => a.frist === 'ja' },
    { id: 'wichtig', label: 'Kasten „Das ist wichtig" (freiwillig)', typ: 'textarea', platzhalter: 'z. B. Ohne Check-in sieht der Trainer nicht, wer da ist.' },
    { id: 'knopf', label: 'Beschriftung des Knopfs', typ: 'text', maxLaenge: 40, platzhalter: 'z. B. Jetzt anmelden', pflicht: (_d, a) => a.link === 'ja', zeigen: (_d, a) => a.link === 'ja' },
    { id: 'url', label: 'Link des Knopfs', typ: 'url', platzhalter: 'https://…', pflicht: (_d, a) => a.link === 'ja', zeigen: (_d, a) => a.link === 'ja' },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('🔔'),
  ],
  start: () => ({ ...START_GESTALTUNG, thema: '', titel: '', text: '', frist: '', wichtig: '', knopf: '', url: '', anrede: 'mitglieder-eltern', schluss: 'Danke Euch — bis bald auf der Matte!' }),
  betreff: (d) => [
    `Kurze Erinnerung: ${d.thema || d.titel}`,
    d.frist ? `${em(d, '⏰')}${d.thema || 'Erinnerung'} – bis ${datumKurz(d.frist)}` : `${em(d, '🔔')}${d.titel || d.thema}`,
    `Bitte dran denken: ${d.thema || d.titel}`,
  ],
  vorschautext: (d) => (d.frist ? `Bis ${datumLang(d.frist)} — ` : '') + (d.titel || d.thema),
  bauen: (d, k) => {
    const sections: MailSection[] = [];
    if (d.frist) sections.push({ type: 'facts', rows: fakten(d, [{ label: 'Worum', value: d.thema }, { label: 'Bis', value: datumLang(d.frist) }]) });
    if (d.wichtig?.trim()) sections.push({ type: 'box', tone: 'important', title: `${em(d, '⚠️')}Das ist wichtig`, text: d.wichtig });
    if (d.url?.trim() && d.knopf?.trim()) sections.push({ type: 'cta', title: d.titel || d.thema, buttonText: d.knopf, url: d.url });
    return {
      title: d.titel || d.thema,
      preheader: d.frist ? `Bis ${datumLang(d.frist)}` : d.thema,
      hero: { emoji: kopfSymbol(d, '🔔', '🔔⏰'), badge: `Erinnerung · ${ortText(k)}`, title: d.titel || d.thema, highlight: d.frist ? `${em(d, '⏰')}Bis ${datumLang(d.frist)}` : undefined, ...banner(d, d.thema || 'Erinnerung') },
      greeting: anrede(d),
      intro: d.text,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

const EINLADUNG: Vorlage = {
  id: 'einladung',
  name: 'Einladung zu Event, Seminar oder Lehrgang',
  kurz: 'Zu etwas einladen: Seminar, Lehrgang, Feier, Aktionstag, Ausstellung.',
  wann: 'Für Veranstaltungen der Sportschule. Größere Events legt Ihr im Event-Tool an — von dort kommt der fertige Mail-Entwurf.',
  emoji: '🥋🎉',
  kategorie: 'einladung',
  vorbild: ['Weihnachtsfeier-Einladungen (14.09.2026)', 'Samurai-Ausstellung Steglitz (20.08.2026)', 'Notwehr-Seminar und Erste-Hilfe-Kurs (Entwürfe 29.09.2026)'],
  vorlauf: 'Zwei bis vier Wochen vor der Veranstaltung — mindestens aber vier Werktage vor dem Wunschtermin.',
  fragen: [
    {
      id: 'eventtool',
      frage: 'Ist die Veranstaltung im Event-Tool angelegt?',
      warum: 'Events mit Anmeldung, Zahlung oder mehreren Sportschulen laufen über das Event-Tool mit eigener Freigabe. Von dort kommt der Mail-Entwurf schon fertig — doppelt schreiben lohnt nicht.',
      optionen: [
        { wert: 'klein', label: 'Nein — kleine Veranstaltung unserer Sportschule', folge: 'ok' },
        { wert: 'ja', label: 'Ja, ist dort freigegeben', folge: 'hinweis', text: 'Dann liegt der Mail-Entwurf aus dem Event-Tool vielleicht schon bei der Redaktion. Schreib unten im Nachrichtenfeld dazu, welches Event es ist.' },
        { wert: 'gross', label: 'Nein — aber es ist ein größeres Event', folge: 'klaeren', text: 'Bitte zuerst im Event-Tool anlegen und freigeben lassen. Anmeldung, Zahlung und Mail entstehen dann dort.' },
      ],
    },
    {
      id: 'anmeldung',
      frage: 'Wie meldet man sich an?',
      warum: 'Die Mail muss sagen, was zu tun ist. Ein Anmeldelink muss vor dem Versand funktionieren.',
      optionen: [
        { wert: 'online', label: 'Online, über einen Link', folge: 'ok' },
        { wert: 'tresen', label: 'Am Tresen / vor Ort', folge: 'ok' },
        { wert: 'keine', label: 'Keine Anmeldung nötig', folge: 'ok' },
      ],
    },
    {
      id: 'link-live',
      frage: 'Ist der Anmeldelink freigeschaltet und getestet?',
      warum: 'Ein toter Link in einer Mail an Hunderte Postfächer ist das Schlimmste, was passieren kann.',
      zeigen: (a) => a.anmeldung === 'online',
      optionen: [
        { wert: 'ja', label: 'Ja, selbst ausprobiert', folge: 'ok' },
        { wert: 'nein', label: 'Noch nicht', folge: 'klaeren', text: 'Erst den Link freischalten und einmal selbst durchklicken, dann einreichen.' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    { id: 'anlass', label: 'Was ist es?', typ: 'text', pflicht: true, maxLaenge: 50, platzhalter: 'z. B. Notwehr-Seminar' },
    { id: 'titel', label: 'Überschrift', typ: 'text', pflicht: true, maxLaenge: 70, platzhalter: 'z. B. Selbstbewusst im Ernstfall' },
    { id: 'untertitel', label: 'Unterzeile (freiwillig)', typ: 'text', maxLaenge: 80, platzhalter: 'z. B. Für alle ab 9 Jahren – nur 40 Plätze' },
    { id: 'datum', label: 'Datum', typ: 'datum', pflicht: true },
    { id: 'uhrzeit', label: 'Uhrzeit', typ: 'text', pflicht: true, maxLaenge: 40, platzhalter: 'z. B. 10:00–14:00' },
    { id: 'ort', label: 'Ort', typ: 'text', pflicht: true, maxLaenge: 120, hinweis: 'Voreingestellt: die Adresse Deiner Sportschule.' },
    { id: 'fuerwen', label: 'Für wen?', typ: 'text', maxLaenge: 80, platzhalter: 'z. B. Mitglieder ab 9 Jahren, Eltern willkommen' },
    { id: 'kosten', label: 'Kosten', typ: 'text', maxLaenge: 80, platzhalter: 'z. B. kostenlos · 15 € für Nicht-Mitglieder' },
    { id: 'einleitung', label: 'Einleitung', typ: 'textarea', pflicht: true, platzhalter: 'Zwei, drei Sätze, die Lust machen.' },
    { id: 'punkte', label: '„Das erwartet Euch" (eine Zeile je Punkt, „Titel: Text")', typ: 'zeilen', platzhalter: 'Praxis statt Theorie: Befreiungen aus Griffen, die wirklich funktionieren\nFür alle: Kein Vorwissen nötig' },
    { id: 'knopf', label: 'Beschriftung des Anmeldeknopfs', typ: 'text', maxLaenge: 40, platzhalter: 'z. B. Jetzt Platz sichern', pflicht: (_d, a) => a.anmeldung === 'online', zeigen: (_d, a) => a.anmeldung === 'online' },
    { id: 'url', label: 'Anmeldelink', typ: 'url', platzhalter: 'https://…', pflicht: (_d, a) => a.anmeldung === 'online', zeigen: (_d, a) => a.anmeldung === 'online' },
    { id: 'schluss_anmeldung', label: 'Anmeldeschluss (freiwillig)', typ: 'datum', zeigen: (_d, a) => a.anmeldung !== 'keine' },
    { id: 'hinweis', label: 'Wichtiger Hinweis (freiwillig)', typ: 'textarea', platzhalter: 'z. B. Bitte bequeme Sportkleidung mitbringen.' },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('🎉'),
  ],
  start: (k) => ({ ...START_GESTALTUNG,
    anlass: '', titel: '', untertitel: '', datum: '', uhrzeit: '',
    ort: k.schulen.length === 1 ? `Randori-Pro ${k.schulen[0].name}, ${k.schulen[0].adresse}` : '',
    fuerwen: '', kosten: '', einleitung: '', punkte: '', knopf: 'Jetzt anmelden', url: '', schluss_anmeldung: '', hinweis: '',
    anrede: 'mitglieder-eltern', schluss: 'Wir freuen uns auf Euch!',
  }),
  betreff: (d) => [
    `${d.anlass || 'Einladung'} am ${datumKurz(d.datum) || '…'} – jetzt anmelden`,
    `${d.titel || d.anlass}: ${datumLang(d.datum)}`,
    `${em(d, '🥋')}${d.anlass || 'Einladung'} – sei dabei am ${datumKurz(d.datum) || '…'}`,
  ],
  vorschautext: (d) => [datumLang(d.datum), uhr(d.uhrzeit), d.kosten].filter(Boolean).join(' · ').slice(0, 90),
  bauen: (d, k) => {
    const ortTeile = (d.ort ?? '').split(',');
    const sections: MailSection[] = [];
    const k1 = karten(d.punkte);
    if (k1.length) sections.push({ type: 'cards', heading: `${em(d, '✨')}Das erwartet Euch`, items: k1 });
    sections.push({ type: 'facts', heading: `${em(d, '📋')}Alle Infos auf einen Blick`, rows: fakten(d, [
      { label: 'Wann', value: [datumLang(d.datum), uhr(d.uhrzeit)].filter(Boolean).join(' · ') },
      { label: 'Wo', value: ortTeile[0]?.trim() ?? '', sub: ortTeile.slice(1).join(',').trim() },
      { label: 'Für wen', value: d.fuerwen },
      { label: 'Kosten', value: d.kosten },
      { label: 'Anmeldeschluss', value: datumLang(d.schluss_anmeldung) },
    ]) });
    if (d.url?.trim()) sections.push({ type: 'cta', title: 'Platz sichern', text: d.schluss_anmeldung ? `Anmeldung bis ${datumLang(d.schluss_anmeldung)}.` : undefined, buttonText: d.knopf || 'Jetzt anmelden', url: d.url, footnote: d.kosten || undefined });
    if (d.hinweis?.trim()) sections.push({ type: 'box', tone: 'important', title: `${em(d, '⚠️')}Bitte beachten`, text: d.hinweis });
    return {
      title: `${d.anlass} – ${d.titel}`,
      preheader: [datumLang(d.datum), uhr(d.uhrzeit)].filter(Boolean).join(' · '),
      hero: { emoji: kopfSymbol(d, '🎉', '🥋🎉'), badge: `${d.anlass || 'Einladung'} · ${datumLang(d.datum) || ortText(k)}`, title: d.titel || d.anlass, subtitle: d.untertitel, highlight: `${em(d, '⚡')}${[datumLang(d.datum), uhr(d.uhrzeit)].filter(Boolean).join(' · ')}`, ...banner(d, d.anlass) },
      greeting: anrede(d),
      intro: d.einleitung,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

const PRUEFUNG: Vorlage = {
  id: 'guertelpruefung',
  name: 'Gürtelprüfung',
  kurz: 'Termin, Gruppen und Uhrzeiten einer Gürtelprüfung — mit Voraussetzungen und Packliste.',
  wann: 'Sobald Prüfer:innen, Gruppen und Zeiten feststehen — am besten drei bis vier Wochen vorher.',
  emoji: '🥋⭐',
  kategorie: 'pruefung',
  vorbild: ['Gürtelprüfung im Mai (07.05.2026)', 'Vorbereitung zur Prüfung (29.04.2026)'],
  vorlauf: 'Drei bis vier Wochen vor der Prüfung, mindestens vier Werktage vor dem Wunschtermin.',
  fragen: [
    {
      id: 'fest',
      frage: 'Stehen Prüfer:innen, Gruppen und Uhrzeiten fest?',
      warum: 'Eltern planen den Tag um die Uhrzeit herum. Eine verschobene Gruppe bedeutet Dutzende Rückfragen.',
      optionen: [
        { wert: 'ja', label: 'Ja', folge: 'ok' },
        { wert: 'nein', label: 'Noch nicht', folge: 'klaeren', text: 'Erst Gruppen und Zeiten festlegen, dann einreichen.' },
      ],
    },
    {
      id: 'zuschauen',
      frage: 'Dürfen Eltern zuschauen?',
      warum: 'Das fragen alle — und es entscheidet, wie voll es in der Halle wird.',
      optionen: [
        { wert: 'ja', label: 'Ja', folge: 'ok' },
        { wert: 'abschluss', label: 'Nur zur Übergabe am Ende', folge: 'ok' },
        { wert: 'nein', label: 'Nein', folge: 'ok' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    { id: 'datum', label: 'Datum', typ: 'datum', pflicht: true },
    { id: 'einlass', label: 'Einlass', typ: 'uhrzeit' },
    { id: 'ort', label: 'Ort', typ: 'text', pflicht: true, maxLaenge: 120 },
    { id: 'gruppen', label: 'Gruppen und Uhrzeiten (eine je Zeile, „Gruppe: Uhrzeit")', typ: 'zeilen', pflicht: true, platzhalter: 'Weiß-Gelb Kids 6–9: 10:00–11:00 Uhr\nGelb-Orange Kids 10–13: 11:15–12:30 Uhr' },
    { id: 'voraussetzungen', label: 'Voraussetzungen (eine je Zeile)', typ: 'zeilen', platzhalter: 'Vorprüfung bestanden\nPrüfungsgebühr bezahlt' },
    { id: 'mitbringen', label: 'Bitte mitbringen (eine je Zeile)', typ: 'zeilen', platzhalter: 'Sauberer Gi mit aktuellem Gürtel\nTrinkflasche\nPrüfungsheft' },
    { id: 'gebuehr', label: 'Prüfungsgebühr (freiwillig)', typ: 'text', maxLaenge: 60, platzhalter: 'z. B. 25 €, bar am Prüfungstag' },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('🥋'),
  ],
  start: (k) => ({ ...START_GESTALTUNG,
    datum: '', einlass: '', ort: k.schulen.length === 1 ? `Randori-Pro ${k.schulen[0].name}, ${k.schulen[0].adresse}` : '',
    gruppen: '', voraussetzungen: '', mitbringen: 'Sauberer Gi mit aktuellem Gürtel\nTrinkflasche', gebuehr: '',
    anrede: 'eltern', schluss: 'Wir drücken allen die Daumen — Ihr schafft das!',
  }),
  betreff: (d, k) => [
    `Gürtelprüfung am ${datumKurz(d.datum) || '…'} in ${ortText(k)} – alle Zeiten`,
    `${em(d, '🥋⭐')}Eure Gürtelprüfung: ${datumLang(d.datum)}`,
    `Prüfungstag in ${ortText(k)}: Gruppen, Uhrzeiten und Packliste`,
  ],
  vorschautext: (d) => `${datumLang(d.datum)} — Gruppen, Uhrzeiten und was Ihr mitbringen müsst.`,
  bauen: (d, k, a) => {
    const ortTeile = (d.ort ?? '').split(',');
    const gruppen = karten(d.gruppen);
    const sections: MailSection[] = [
      { type: 'facts', heading: `${em(d, '📋')}Der Prüfungstag`, rows: fakten(d, [
        { label: 'Datum', value: datumLang(d.datum) },
        { label: 'Einlass', value: uhr(d.einlass) },
        { label: 'Wo', value: ortTeile[0]?.trim() ?? '', sub: ortTeile.slice(1).join(',').trim() },
        { label: 'Gebühr', value: d.gebuehr },
      ]) },
    ];
    if (gruppen.length) sections.push({ type: 'cards', heading: `${em(d, '🕐')}Gruppen und Uhrzeiten`, items: gruppen });
    const vor = zeilen(d.voraussetzungen);
    if (vor.length) sections.push({ type: 'list', heading: `${em(d, '📋')}Voraussetzung für die Prüfung`, items: vor });
    const mit = zeilen(d.mitbringen);
    if (mit.length) sections.push({ type: 'list', heading: `${em(d, '🎒')}Bitte mitbringen`, items: mit });
    const zuschauen: Record<string, string> = {
      ja: 'Eltern und Familie sind herzlich eingeladen zuzuschauen.',
      abschluss: 'Zur Gürtelübergabe am Ende sind Eltern herzlich willkommen — während der Prüfung bleibt die Halle den Prüflingen vorbehalten.',
      nein: 'Während der Prüfung bleibt die Halle den Prüflingen vorbehalten — danke für Euer Verständnis.',
    };
    const zu = a?.zuschauen ?? '';
    if (zuschauen[zu]) sections.push({ type: 'box', title: `${em(d, '👀')}Zuschauen`, text: zuschauen[zu] });
    return {
      title: `Gürtelprüfung ${datumLang(d.datum)}`,
      preheader: `${datumLang(d.datum)} — alle Gruppen und Zeiten`,
      hero: { emoji: kopfSymbol(d, '🥋', '🥋⭐'), badge: `Gürtelprüfung · ${ortText(k)}`, title: 'Eure Gürtelprüfung', highlight: `${em(d, '⚡')}${datumLang(d.datum)}${d.einlass ? ` · Einlass ${uhr(d.einlass)}` : ''}`, ...banner(d, 'Gürtelprüfung') },
      greeting: anrede(d),
      intro: `bald ist es so weit: Am **${datumLang(d.datum)}** zeigen unsere Prüflinge, was sie gelernt haben. Hier findet Ihr alle Gruppen, Uhrzeiten und was Ihr mitbringen müsst.`,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

const ELTERN: Vorlage = {
  id: 'eltern-info',
  name: 'Info an die Eltern',
  kurz: 'Eltern informieren: Ablauf, Abholung, Ausrüstung, Termine rund um die Kids.',
  wann: 'Wenn sich eine Mail vor allem an Eltern richtet. Wähl im Schritt „Empfänger" die passende Familien-Gruppe.',
  emoji: '👨‍👩‍👧🥋',
  kategorie: 'eltern',
  vorbild: ['Kinderturnier: Infomail an alle angemeldeten Familien (24.09.2026)', 'Trainingslager: Ankunft und letzte Infos (11.08.2026)'],
  vorlauf: 'Mindestens vier Werktage vor dem Wunschtermin.',
  fragen: [
    {
      id: 'gruppe',
      frage: 'Betrifft es alle Kinder oder eine bestimmte Gruppe?',
      warum: 'Die Empfängergruppen richten sich nach dem Alter der Kinder im Haushalt. Passt keine, legt die Redaktion eine an — das dauert etwas.',
      optionen: [
        { wert: 'alle', label: 'Alle Familien der Sportschule', folge: 'ok' },
        { wert: 'gruppe', label: 'Eine Altersgruppe (z. B. 6–13 Jahre)', folge: 'ok' },
        { wert: 'fehlt', label: 'Eine Gruppe, die es noch nicht gibt', folge: 'hinweis', text: 'Schreib der Redaktion im letzten Schritt, wen die Mail erreichen soll — sie legt die Gruppe an oder passt sie an.' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    { id: 'thema', label: 'Thema (kleine Zeile oben)', typ: 'text', pflicht: true, maxLaenge: 50, platzhalter: 'z. B. Ferienprogramm Kids' },
    { id: 'titel', label: 'Überschrift', typ: 'text', pflicht: true, maxLaenge: 70 },
    { id: 'text', label: 'Die Nachricht', typ: 'textarea', pflicht: true, platzhalter: 'Worum geht es? **fett** für das Wichtigste.' },
    { id: 'schritte', label: 'Ablauf / nächste Schritte (eine je Zeile, freiwillig)', typ: 'zeilen', platzhalter: 'Bis Freitag anmelden\nAm Montag um 9 Uhr bringen\nUm 15 Uhr abholen' },
    { id: 'packliste', label: 'Packliste (eine je Zeile, freiwillig)', typ: 'zeilen' },
    { id: 'wichtig', label: 'Wichtiger Hinweis (freiwillig)', typ: 'textarea' },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('👨‍👩‍👧'),
  ],
  start: () => ({ ...START_GESTALTUNG, thema: '', titel: '', text: '', schritte: '', packliste: '', wichtig: '', anrede: 'eltern', schluss: 'Danke Euch — bis bald auf der Matte!' }),
  betreff: (d) => [
    `${d.thema || 'Info für Eltern'}: ${d.titel}`,
    `Liebe Eltern: ${d.titel || d.thema}`,
    `${em(d, '👨‍👩‍👧')}${d.titel || d.thema} – alle Infos`,
  ],
  vorschautext: (d) => (d.titel || d.thema || '').slice(0, 90),
  bauen: (d, k) => {
    const sections: MailSection[] = [];
    const s = zeilen(d.schritte);
    if (s.length) sections.push({ type: 'steps', heading: `${em(d, '🗓️')}So läuft es ab`, items: s });
    const p = zeilen(d.packliste);
    if (p.length) sections.push({ type: 'list', heading: `${em(d, '🎒')}Packliste`, items: p });
    if (d.wichtig?.trim()) sections.push({ type: 'box', tone: 'important', title: `${em(d, '⚠️')}Wichtig`, text: d.wichtig });
    return {
      title: d.titel || d.thema,
      preheader: d.titel,
      hero: { emoji: kopfSymbol(d, '👨‍👩‍👧', '👨‍👩‍👧🥋'), badge: `${d.thema || 'Eltern-Info'} · ${ortText(k)}`, title: d.titel || d.thema, ...banner(d, d.thema) },
      greeting: anrede(d),
      intro: d.text,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

const FREI: Vorlage = {
  id: 'freie-nachricht',
  name: 'Allgemeine Info',
  kurz: 'Für alles, was in keine andere Vorlage passt — mit Kasten und Knopf nach Wunsch.',
  wann: 'Wenn keine andere Vorlage passt. Je kürzer, desto besser: eine Botschaft pro Mail.',
  emoji: '📣',
  kategorie: 'info',
  vorbild: ['Aufbau aller Mails seit August 2026: roter Kopf, Anrede, Text, Kasten, Knopf, Signatur'],
  vorlauf: 'Mindestens vier Werktage vor dem Wunschtermin.',
  fragen: [
    {
      id: 'link',
      frage: 'Soll ein Knopf in der Mail stehen?',
      warum: 'Ein Knopf führt direkt ans Ziel — er muss vor dem Versand funktionieren.',
      optionen: [
        { wert: 'nein', label: 'Nein', folge: 'ok' },
        { wert: 'ja', label: 'Ja, und der Link funktioniert', folge: 'ok' },
        { wert: 'bald', label: 'Ja, aber der Link ist noch nicht fertig', folge: 'klaeren', text: 'Erst den Link fertig machen und testen, dann einreichen.' },
      ],
    },
    ...FRAGEN_IMMER,
  ],
  felder: [
    { id: 'thema', label: 'Thema (kleine Zeile oben)', typ: 'text', pflicht: true, maxLaenge: 50 },
    { id: 'titel', label: 'Überschrift', typ: 'text', pflicht: true, maxLaenge: 70 },
    { id: 'untertitel', label: 'Unterzeile (freiwillig)', typ: 'text', maxLaenge: 80 },
    { id: 'text', label: 'Text', typ: 'textarea', pflicht: true, platzhalter: 'Absätze mit einer Leerzeile trennen. **fett** für das Wichtigste.' },
    { id: 'kasten_titel', label: 'Kasten: Überschrift (freiwillig)', typ: 'text', maxLaenge: 60 },
    { id: 'kasten_text', label: 'Kasten: Text', typ: 'textarea', zeigen: (d) => !!d.kasten_titel?.trim() },
    { id: 'knopf', label: 'Beschriftung des Knopfs', typ: 'text', maxLaenge: 40, pflicht: (_d, a) => a.link === 'ja', zeigen: (_d, a) => a.link === 'ja' },
    { id: 'url', label: 'Link des Knopfs', typ: 'url', pflicht: (_d, a) => a.link === 'ja', zeigen: (_d, a) => a.link === 'ja' },
    FELD_ANREDE,
    FELD_SCHLUSS,
    ...gestaltung('📣'),
  ],
  start: () => ({ ...START_GESTALTUNG, thema: '', titel: '', untertitel: '', text: '', kasten_titel: '', kasten_text: '', knopf: '', url: '', anrede: 'mitglieder-eltern', schluss: 'Wir freuen uns auf Euch — bis bald!' }),
  betreff: (d) => [d.titel || d.thema, `${em(d, '📣')}${d.thema ? `${d.thema}: ` : ''}${d.titel || ''}`, `Neu bei Randori-Pro: ${d.titel || d.thema}`],
  vorschautext: (d) => (d.untertitel || d.titel || '').slice(0, 90),
  bauen: (d, k) => {
    const sections: MailSection[] = [];
    if (d.kasten_titel?.trim()) sections.push({ type: 'box', title: d.kasten_titel, text: d.kasten_text ?? '' });
    if (d.url?.trim() && d.knopf?.trim()) sections.push({ type: 'cta', title: d.titel, buttonText: d.knopf, url: d.url });
    return {
      title: d.titel || d.thema,
      preheader: d.untertitel || d.titel,
      hero: { emoji: kopfSymbol(d, '📣', '📣✨'), badge: `${d.thema || 'Info'} · ${ortText(k)}`, title: d.titel || d.thema, subtitle: d.untertitel, ...banner(d, d.thema) },
      greeting: anrede(d),
      intro: d.text,
      sections,
      closing: schluss(d),
      signature: { greeting: 'Sportliche Grüße', team: teamName(k, d) },
      contact: kontakt(k),
      emojis: emojiStufe(d),
    };
  },
};

export const VORLAGEN: Vorlage[] = [KURZFRISTIG, STUNDENPLAN, SCHLIESSZEIT, ERINNERUNG, EINLADUNG, PRUEFUNG, ELTERN, FREI];

export function vorlage(id: string | null | undefined): Vorlage | null {
  return VORLAGEN.find((v) => v.id === id) ?? null;
}

export const KATEGORIE_TEXT: Record<Kategorie, string> = {
  kurzfristig: 'Kurzfristig',
  stundenplan: 'Stundenplan',
  einladung: 'Einladung',
  erinnerung: 'Erinnerung',
  eltern: 'Eltern',
  pruefung: 'Prüfung',
  info: 'Info',
};

/**
 * Bis wann die Mail spätestens draußen sein muss — sonst kommt sie zu spät
 * (der Kurs ist schon ausgefallen, die Frist schon vorbei).
 */
export function stichtag(v: Vorlage, d: Daten): { datum: string; was: string } | null {
  const feld: Record<string, [string, string][]> = {
    'kurzfristige-aenderung': [['datum', 'der Termin der Änderung']],
    stundenplan: [['ab', 'der Stichtag des neuen Stundenplans']],
    schliesszeit: [['von', 'der Beginn der Schließzeit']],
    erinnerung: [['frist', 'die Frist']],
    einladung: [['schluss_anmeldung', 'der Anmeldeschluss'], ['datum', 'die Veranstaltung']],
    guertelpruefung: [['datum', 'die Prüfung']],
  };
  for (const [id, was] of feld[v.id] ?? []) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(d[id] ?? '')) return { datum: d[id], was };
  }
  return null;
}

// ── Prüfen ──────────────────────────────────────────────────────────────────

export function sichtbareFragen(v: Vorlage, a: Antworten): Frage[] {
  return v.fragen.filter((f) => !f.zeigen || f.zeigen(a));
}

export function sichtbareFelder(v: Vorlage, d: Daten, a: Antworten): Feld[] {
  return v.felder.filter((f) => !f.zeigen || f.zeigen(d, a));
}

export interface Befund {
  /** offene Punkte — so geht die Mail nicht raus */
  klaeren: string[];
  /** Hinweise — gehen mit an die Redaktion */
  hinweise: string[];
  /** noch nicht beantwortete Fragen */
  unbeantwortet: string[];
}

export function vorabBefund(v: Vorlage, a: Antworten): Befund {
  const b: Befund = { klaeren: [], hinweise: [], unbeantwortet: [] };
  for (const f of sichtbareFragen(v, a)) {
    const o = f.optionen.find((x) => x.wert === a[f.id]);
    if (!o) { b.unbeantwortet.push(f.frage); continue; }
    if (o.folge === 'klaeren') b.klaeren.push(`${f.frage} — ${o.label}. ${o.text ?? ''}`.trim());
    if (o.folge === 'hinweis' && o.text) b.hinweise.push(o.text);
  }
  return b;
}

function istLeer(s: string | undefined): boolean {
  return !(s ?? '').trim();
}

/** Fehlende Pflichtfelder und ungültige Angaben (Link, Datum). */
export function inhaltFehler(v: Vorlage, d: Daten, a: Antworten): string[] {
  const out: string[] = [];
  for (const f of sichtbareFelder(v, d, a)) {
    const pflicht = typeof f.pflicht === 'function' ? f.pflicht(d, a) : !!f.pflicht;
    const wert = f.typ === 'auswahl' && istLeer(d[f.id]) && f.standard ? f.standard : d[f.id];
    if (pflicht && istLeer(wert)) { out.push(`„${f.label}" fehlt`); continue; }
    if (!istLeer(wert) && (f.typ === 'url' || f.typ === 'banner') && !/^https:\/\/\S+\.\S+/i.test(wert.trim())) {
      out.push(`„${f.label}": bitte einen vollständigen Link mit https:// eintragen`);
    }
    if (!istLeer(wert) && f.maxLaenge && wert.length > f.maxLaenge) out.push(`„${f.label}" ist zu lang (höchstens ${f.maxLaenge} Zeichen)`);
    if (f.typ === 'datum' && !istLeer(wert)) {
      const heute = new Date(); heute.setHours(0, 0, 0, 0);
      if (new Date(`${wert}T00:00:00`) < heute) out.push(`„${f.label}" liegt in der Vergangenheit`);
    }
  }
  if (v.id === 'schliesszeit' && d.von && d.bis && d.bis < d.von) out.push('„bis einschließlich" liegt vor „Geschlossen von"');
  if (v.id === 'schliesszeit' && d.bis && d.wieder && d.wieder <= d.bis) out.push('„Erstes Training danach" muss nach der Schließzeit liegen');
  return out;
}

/** Stolpersteine, die die Redaktion sonst zurückschicken würde. */
export function stilHinweise(betreff: string, html: string): string[] {
  const out: string[] = [];
  if (betreff.length > 70) out.push(`Der Betreff hat ${betreff.length} Zeichen — auf dem Handy werden etwa 40 bis 50 angezeigt.`);
  if (/!{2,}/.test(betreff) || /[A-ZÄÖÜ]{6,}/.test(betreff)) out.push('Betreff ohne Großbuchstaben-Wörter und mehrere Ausrufezeichen — so landet die Mail seltener im Spam.');
  const offen = offenePlatzhalter(html);
  if (offen.length) out.push(`In der Mail steht noch ein Platzhalter in eckigen Klammern: ${offen.slice(0, 3).join(', ')}.`);
  if (/bleibt gesund/i.test(html)) out.push('„Bleibt gesund" verwenden wir nicht mehr (Redaktions-Regel).');
  return out;
}
